/**
 * Whether a data set covers the borough a property sits in.
 *
 * Some sources only exist for part of the city. DOT Liens is the first: the
 * sidewalk lien book is indexed by the New York County clerk and nowhere else,
 * so a property in Brooklyn or Queens is not "clean", it is out of coverage.
 *
 * Those two states must never look the same. Showing an empty DOT Liens table
 * for a Brooklyn building tells its owner there are no sidewalk liens against
 * it, which is a claim we cannot make. Hiding the data set entirely is the
 * honest rendering: we say nothing rather than something false.
 *
 * Coverage is declared in dataConfig via `coveredBoroughs` so the rule lives
 * beside the rest of a data set's configuration and not inside a component.
 * A data set without the key is assumed to cover the whole city, which is what
 * every other data set does.
 */

import { dataConfig } from 'data/dataConfig';

/**
 * DOB borough numbers, as stored on properties.borough.
 */
export const BOROUGH_MANHATTAN = 1;

/**
 * Whether this data set applies to this property at all.
 *
 * Returns TRUE when coverage cannot be determined - an unknown borough must not
 * hide a data set, because a missing field is not evidence of non-coverage and
 * hiding real liens is worse than showing an empty table.
 *
 * @param {string} dataSet  Key into dataConfig.
 * @param {object} propertyData  The property overview payload.
 * @returns {boolean}
 */
export function isDataSetInCoveredBorough(dataSet, propertyData) {
	const covered = dataConfig[dataSet]?.coveredBoroughs;
	if (!Array.isArray(covered) || covered.length === 0) {
		return true;
	}

	const borough = Number(propertyData?.borough);
	if (!Number.isFinite(borough) || borough === 0) {
		return true;
	}

	return covered.includes(borough);
}
